import { useState, type CSSProperties } from 'react';
import { AnimatePresence } from 'motion/react';
import { Button } from '../../design-system/Button';
import { ShieldCheck } from '../../design-system';
import { SleekButton } from '../../theme';
import { useFairnessEnabled } from '../useFairnessEnabled';
import { useFairnessTicket } from '../useFairnessTicket';
import { FairnessInfoModal } from './FairnessInfoModal';

/** Which odds surface the section sits in; each reads that surface's own knobs. */
export type ProvablyFairSkin = 'sleek' | 'modern' | 'classic';

export interface ProvablyFairSectionProps {
  /** The pack on screen; its ticket is kept fresh while the section is mounted. */
  offerId: string | number | null | undefined;
  skin: ProvablyFairSkin;
}

const BLURB = 'Every rarity roll below follows from the two seeds and the odds Repackz signed before you bought.';

const SKINS: Record<ProvablyFairSkin, { className: string; style: CSSProperties; bodyStyle: CSSProperties }> = {
  // Under the rarity rows of the odds sheet / popover (partner Figma 2026-10-08).
  sleek: {
    className: 'mt-8 border-t pt-6',
    style: { borderColor: 'var(--co-OddsSheet-surface, #25262C)', color: 'var(--co-OddsSheet-text, #FFFFFF)' },
    bodyStyle: { color: 'var(--co-OddsSheet-text, #FFFFFF)', opacity: 0.8 },
  },
  // Inside the OddsBreakdown card (modal and popover).
  modern: {
    className: 'border-t pt-3',
    style: { borderColor: 'var(--co-OddsBreakdown-barTrack, rgba(255,255,255,0.08))', color: 'var(--co-OddsBreakdown-barNameColor, #ffffff)' },
    bodyStyle: { color: 'var(--co-OddsBreakdown-barRangeColor, rgba(255,255,255,0.5))' },
  },
  // Classic has no odds dialog: one row at the foot of the odds card (see `CompactRow`).
  classic: {
    className: 'mt-4 border-t pt-3',
    style: {
      borderColor: 'color-mix(in srgb, var(--co-OddsCard-valueColor, #ffffff) 15%, transparent)',
      color: 'var(--co-OddsCard-valueColor, #ffffff)',
    },
    bodyStyle: {},
  },
};

/**
 * Odds: "Provably Fair · Learn More". Renders nothing unless the operator has
 * provably fair on — and then mounts nothing either (no session read, no
 * ticket timers), so other operators' odds are untouched.
 */
export function ProvablyFairSection({ offerId, skin }: ProvablyFairSectionProps) {
  const enabled = useFairnessEnabled();
  if (!enabled) return null;
  return <SectionBody offerId={offerId != null && offerId !== '' ? String(offerId) : null} skin={skin} />;
}

function LearnMore({ skin, onClick }: { skin: ProvablyFairSkin; onClick: () => void }) {
  if (skin === 'sleek') {
    return (
      <SleekButton name="FairnessLearnMoreButton" onClick={onClick} aria-haspopup="dialog">
        Learn More
      </SleekButton>
    );
  }
  if (skin === 'modern') {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-haspopup="dialog"
        // The bar-track wash alone is near-invisible on the glass card, so the
        // pill also carries an outline in the text colour.
        className="h-11 rounded-full border border-solid px-5 text-[15px] font-semibold transition-opacity hover:opacity-80 active:scale-95"
        style={{
          color: 'var(--co-OddsBreakdown-modValueColor, #ffffff)',
          backgroundColor: 'var(--co-OddsBreakdown-barTrack, rgba(255,255,255,0.08))',
          borderColor: 'color-mix(in srgb, var(--co-OddsBreakdown-modValueColor, #ffffff) 40%, transparent)',
        }}
      >
        Learn More
      </button>
    );
  }
  return (
    <Button variant="glass-frosted" size="sm" onClick={onClick} aria-haspopup="dialog">
      Learn More
    </Button>
  );
}

/** Classic: "Provably Fair 🛡 ………… Learn More" in one line inside the odds card. */
function CompactRow({ onClick }: { onClick: () => void }) {
  const look = SKINS.classic;
  return (
    <div className={`flex items-center justify-between gap-3 ${look.className}`} style={look.style}>
      <span className="flex items-center gap-2 text-[15px] font-semibold leading-[20px]">
        Provably Fair
        <ShieldCheck size={18} />
      </span>
      <LearnMore skin="classic" onClick={onClick} />
    </div>
  );
}

function SectionBody({ offerId, skin }: { offerId: string | null; skin: ProvablyFairSkin }) {
  const ticket = useFairnessTicket(offerId);
  const [open, setOpen] = useState(false);
  const look = SKINS[skin];
  const show = () => setOpen(true);

  // No inspect name of its own: a Constructor click lands on the odds surface it sits in.
  return (
    <>
      {skin === 'classic' ? (
        <CompactRow onClick={show} />
      ) : (
        <section aria-label="Provably fair" className={`flex flex-col gap-3 ${look.className}`} style={look.style}>
          <h3 className="flex items-center gap-2 text-[18px] font-semibold leading-[24px] tracking-[-0.4px]">
            Provably Fair
            <ShieldCheck size={20} />
          </h3>
          <p className="text-[15px] leading-[21px]" style={look.bodyStyle}>
            {BLURB}
          </p>
          <div>
            <LearnMore skin={skin} onClick={show} />
          </div>
        </section>
      )}
      <AnimatePresence>{open && <FairnessInfoModal ticket={ticket} onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
}
